// The one money-conversion function (spec §3.4 "One conversion function").
//
// `toMinorUnits`/`isExactMinor` convert a legacy DOUBLE major-unit price (or
// a new entry/payment's `amount: number` wire input) into BIGINT minor
// units. The contract: finite non-negative numbers only; decimal half-up
// rounding of the DECIMAL representation (never a binary-float
// multiplication, never `Number.EPSILON`); a number input whose minor
// result would exceed `Number.MAX_SAFE_INTEGER` is rejected — aggregates
// built from many such conversions stay `bigint`/SQL `numeric` and are
// unrestricted by that ceiling, they only have to fit PostgreSQL BIGINT
// (`assertFitsBigint`).
//
// Parsing strategy: `Number.prototype.toString()` returns the SHORTEST
// decimal string that round-trips back to the same IEEE-754 double — for a
// literal like `1.005` that is `"1.005"`, not the double's true binary value
// (~1.00499999999999989...). Reading digits off that string (falling back to
// expanding scientific notation) is exactly "parse the decimal
// representation" per the spec — it is what lets `1.005 -> 101n` round
// half-up from the digit a human typed, instead of from float noise.
import { Currency } from '../enums/payments';

/** Exhaustive by construction: `Record<Currency, number>` fails to compile
 *  if a new `Currency` member is ever added without an entry here. */
export const CURRENCY_MINOR_EXPONENT: Record<Currency, number> = {
  [Currency.RSD]: 2,
  [Currency.EUR]: 2,
  [Currency.USD]: 2,
  [Currency.BAM]: 2,
};

// `n`-suffixed bigint literals need an ES2020+ target; this module is also
// typechecked under the frontend's ES6 target (no isolated module boundary
// between @mfw/common source and its consumers), so every bigint here is
// built with `BigInt(...)` instead.
const ZERO = BigInt(0);
const ONE = BigInt(1);
const TWO = BigInt(2);
const TEN = BigInt(10);

/** `10n ** exponent` without the `**` operator on bigint — also ES2016+
 *  only, unavailable under the frontend's ES6 target. `exponent` is a small
 *  non-negative integer (a decimal-digit count), so a plain multiplication
 *  loop costs nothing worth optimizing. */
function bigintPow10(exponent: number): bigint {
  let result = ONE;
  for (let i = 0; i < exponent; i += 1) {
    result *= TEN;
  }
  return result;
}

/** PostgreSQL's signed `BIGINT` ceiling — the range a persisted minor-unit
 *  amount (including aggregates, which are NOT bounded by
 *  `Number.MAX_SAFE_INTEGER`) must still fit. Built from a string: the
 *  value itself is above `Number.MAX_SAFE_INTEGER`, so `BigInt(9223...)`
 *  would already have lost precision as a `Number` before conversion. */
export const MAX_BIGINT_MINOR = BigInt('9223372036854775807');
const MIN_BIGINT_MINOR = -MAX_BIGINT_MINOR - ONE;

/** Throws when `value` would not fit a PostgreSQL signed `BIGINT` column.
 *  Use before persisting any aggregate/derived minor-unit amount that did
 *  not come from `toMinorUnits` (which already enforces the tighter
 *  `Number.MAX_SAFE_INTEGER` bound for its own number input). */
export function assertFitsBigint(value: bigint): void {
  if (value < MIN_BIGINT_MINOR || value > MAX_BIGINT_MINOR) {
    throw new RangeError(
      `assertFitsBigint: ${value.toString()} does not fit a PostgreSQL signed BIGINT`,
    );
  }
}

const DECIMAL_PATTERN = /^(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i;

interface ParsedMinor {
  /** The full-precision minor-unit value, rounded half-up from the decimal
   *  digits (never from a binary-float multiplication). */
  minor: bigint;
  /** `true` when rounding needed no adjustment — every discarded digit
   *  (below the currency's minor exponent) was zero. */
  exact: boolean;
}

/** Parses `amount`'s decimal-string representation into `coefficient *
 *  10^exponent` and immediately projects it onto `currency`'s minor-unit
 *  scale, rounding half-up on the digits (magnitude-based, never
 *  `Number.EPSILON`). Returns `null` for anything outside the "finite
 *  non-negative number, known currency" contract — NaN, +/-Infinity,
 *  negative amounts, or an unparseable numeric string (should not happen for
 *  a real `number`, but this function must never throw internally so both
 *  public functions can decide their own failure behaviour). */
function parseToMinor(amount: number, currency: Currency): ParsedMinor | null {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  if (amount < 0) return null;
  const minorExponent = CURRENCY_MINOR_EXPONENT[currency];
  if (minorExponent === undefined) return null;

  const text = amount.toString();
  const match = DECIMAL_PATTERN.exec(text);
  if (!match) return null;
  const [, intPart, fracPart = '', sciExp] = match;
  const digits = intPart + fracPart;
  // digits is never empty: DECIMAL_PATTERN requires at least one leading digit
  const coefficient = BigInt(digits);
  const exponent = -fracPart.length + (sciExp ? Number(sciExp) : 0);

  const totalExponent = exponent + minorExponent;
  if (totalExponent >= 0) {
    return {
      exact: true,
      minor: coefficient * bigintPow10(totalExponent),
    };
  }
  const divisor = bigintPow10(-totalExponent);
  const quotient = coefficient / divisor;
  const remainder = coefficient % divisor;
  const roundedUp = remainder * TWO >= divisor;
  return {
    exact: remainder === ZERO,
    minor: roundedUp ? quotient + ONE : quotient,
  };
}

const MAX_SAFE_MINOR = BigInt(Number.MAX_SAFE_INTEGER);

/** Converts a finite non-negative major-unit `amount` to minor units,
 *  half-up rounding on the DECIMAL digits at `currency`'s exponent. Throws
 *  `RangeError` for NaN/Infinity/negative input, for a currency outside the
 *  exhaustive map, or when the minor-unit result would exceed
 *  `Number.MAX_SAFE_INTEGER` (spec §3.4). */
export function toMinorUnits(amount: number, currency: Currency): bigint {
  const parsed = parseToMinor(amount, currency);
  if (!parsed) {
    throw new RangeError(
      `toMinorUnits: amount must be a finite non-negative number for a known currency, got ${amount} (${currency})`,
    );
  }
  if (parsed.minor > MAX_SAFE_MINOR) {
    throw new RangeError(
      `toMinorUnits: amount ${amount} converts to a minor-unit value above Number.MAX_SAFE_INTEGER`,
    );
  }
  return parsed.minor;
}

/** `true` when `amount` converts to minor units with NO rounding — i.e. it
 *  carries no more fractional precision than `currency`'s minor exponent.
 *  Never throws: any input outside the finite/non-negative/safe-range
 *  contract simply answers `false` — callers gate new entry/payment inputs
 *  with this BEFORE calling `toMinorUnits`, so an invalid amount should read
 *  as "not exact/not acceptable", not raise. */
export function isExactMinor(amount: number, currency: Currency): boolean {
  const parsed = parseToMinor(amount, currency);
  if (!parsed) return false;
  if (parsed.minor > MAX_SAFE_MINOR) return false;
  return parsed.exact;
}

/**
 * Displays a BIGINT minor-unit amount for a human, honoring the currency's
 * decimal exponent and locale grouping — WITHOUT ever routing the amount
 * through `Number`. A value beyond `Number.MAX_SAFE_INTEGER` must still
 * format exactly, so the amount is split on the currency's exponent as
 * STRING digits (major part / minor part), never parsed into a float, and
 * only the two resulting integer-safe pieces are handed to
 * `Intl.NumberFormat` (once for grouping the major part, never for the
 * minor digits, which are literal and never rounded again).
 *
 * `currency` outside `CURRENCY_MINOR_EXPONENT` (should not happen for a real
 * `Currency`, but the map access is technically partial) falls back to an
 * exponent of 2 rather than throwing — a display helper must not crash a
 * page over a report value.
 */
export function formatMinorAmount(
  minor: string | bigint,
  currency: Currency,
  locale?: string,
): string {
  const exponent = CURRENCY_MINOR_EXPONENT[currency] ?? 2;
  const value = typeof minor === 'bigint' ? minor : BigInt(minor);
  const negative = value < ZERO;
  const digits = (negative ? -value : value).toString();

  const padded = digits.padStart(exponent + 1, '0');
  const majorDigits = exponent === 0 ? padded : padded.slice(0, -exponent);
  const minorDigits = exponent === 0 ? '' : padded.slice(-exponent);

  // `Intl.NumberFormat` formats a `bigint` argument EXACTLY — no float
  // round-trip, no `Number.MAX_SAFE_INTEGER` ceiling — and it groups it per
  // the locale's own convention (which is not always runs of three, e.g.
  // Indian English), so the (unsigned) major part goes through it as a
  // bigint rather than through a hand-rolled regex grouping. The sign is
  // applied afterwards as a plain prefix — deliberately not by negating the
  // bigint before formatting, which would lose the sign whenever the major
  // part is zero (e.g. minor amount `-40` EUR must read `-0.40`, not
  // `0.40`). The minor part is fixed-width literal digits (already
  // rounded — it came straight off the minor-unit string), concatenated
  // as-is after the locale's own decimal separator (read off a throwaway
  // fractional format, since a bigint itself carries no fraction to
  // format).
  const groupedMajor = new Intl.NumberFormat(locale).format(
    BigInt(majorDigits),
  );
  const sign = negative ? '-' : '';
  if (exponent === 0) return `${sign}${groupedMajor}`;

  const decimalSeparator =
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
      .formatToParts(0.1)
      .find((part) => part.type === 'decimal')?.value ?? '.';

  return `${sign}${groupedMajor}${decimalSeparator}${minorDigits}`;
}

/** The largest minor-unit amount whose major-unit `number` still lands on
 *  its OWN cent: `2^k × 10^exponent` for the largest `k` with `2^k ×
 *  10^exponent <= 2^53`. Below `2^k` major units a double's spacing is at
 *  most `2^(k-53)`, i.e. at most one minor unit, so every minor value keeps
 *  a distinct nearest double and `toMinorUnits` reads it back exactly;
 *  above it two neighbouring cents can share one double (at exponent 2:
 *  2^46 major = 7036874417766400 minor, and 70368744177664.01 reads back as
 *  ...02). Never above `Number.MAX_SAFE_INTEGER` (exponent 0). */
function computeExactMinorBound(exponent: number) {
  const scale = bigintPow10(exponent);
  let major = ONE;
  while (major * TWO * scale <= MAX_SAFE_MINOR + ONE) major *= TWO;
  const bound = major * scale;
  return bound < MAX_SAFE_MINOR ? bound : MAX_SAFE_MINOR;
}

/** `computeExactMinorBound`, once per exponent (a handful of values). */
const EXACT_MINOR_BOUND = new Map<number, bigint>();
function exactMinorBound(exponent: number) {
  let bound = EXACT_MINOR_BOUND.get(exponent);
  if (bound === undefined) {
    bound = computeExactMinorBound(exponent);
    EXACT_MINOR_BOUND.set(exponent, bound);
  }
  return bound;
}

/**
 * The exact inverse of `toMinorUnits` for a wire input: a non-negative BIGINT
 * minor-unit amount as the major-unit `number` a form field or an
 * `amount: number` DTO carries (membership-renewal plan, Task 8 — the desk
 * dialog prefills the amount due). Built from the decimal DIGITS, never by a
 * float division, so `toMinorUnits(minorToMajor(x), c) === x` for every `x`
 * up to `exactMinorBound` of the currency's exponent (7036874417766400 at
 * exponent 2 — NOT `Number.MAX_SAFE_INTEGER`: a double that far up is
 * coarser than a cent); anything larger (or negative) throws `RangeError`
 * rather than hand back a number that no longer round-trips.
 */
export function minorToMajor(minor: string | bigint, currency: Currency) {
  const value = typeof minor === 'bigint' ? minor : BigInt(minor);
  const exponent = CURRENCY_MINOR_EXPONENT[currency] ?? 2;
  if (value < ZERO || value > exactMinorBound(exponent)) {
    throw new RangeError(
      `minorToMajor: ${value.toString()} is outside the exact non-negative range`,
    );
  }
  if (exponent === 0) return Number(value.toString());
  const padded = value.toString().padStart(exponent + 1, '0');
  return Number(`${padded.slice(0, -exponent)}.${padded.slice(-exponent)}`);
}

/** Half-up (sign-aware, by magnitude) rounding of an exact `bigint` ratio —
 *  used for aggregate splits (fees, splits) that must stay in `bigint`
 *  throughout, never routed through `Number`. `-5n/2n -> -3n`: the sign is
 *  applied AFTER rounding the absolute ratio half-up, matching monetary
 *  "round half away from zero" convention rather than "round half toward
 *  +Infinity". Throws `RangeError` if `denominator` is not positive — a
 *  ratio's denominator is always a count/total, never zero or negative. */
export function roundMinorRatio(
  numerator: bigint,
  denominator: bigint,
): bigint {
  if (denominator <= ZERO) {
    throw new RangeError(
      `roundMinorRatio: denominator must be positive, got ${denominator.toString()}`,
    );
  }
  const negative = numerator < ZERO;
  const absNumerator = negative ? -numerator : numerator;
  const quotient = absNumerator / denominator;
  const remainder = absNumerator % denominator;
  const rounded = remainder * TWO >= denominator ? quotient + ONE : quotient;
  return negative ? -rounded : rounded;
}
